// چت هوشمند کارجو پلاس - React Component in JavaScript
import React, { useState, useEffect, useRef } from "react";

// --- TypeScript Interfaces for Resume Data ---
interface PersonalInfo {
  full_name: string;
  email_address?: string;
  phone_number?: string;
  linkedin_profile?: string;
  portfolio_url?: string;
  professional_summary?: string;
  ai_generated_short_summary?: string;
}

interface WorkExperience {
  job_title: string;
  company_name: string;
  company_location: string;
  start_date: string;
  end_date: string;
  key_responsibilities_and_achievements: string[];
}

interface Education {
  degree_name: string;
  field_of_study: string;
  institution_name: string;
  institution_location: string;
  graduation_date: string;
}

interface LanguageSkill {
  language_name: string;
  proficiency_level: string;
}

interface Skills {
  technical?: string[];
  soft?: string[];
  languages?: LanguageSkill[];
}

interface Project {
  project_name: string;
  description: string;
  technologies_used?: string[];
  project_link?: string;
}

interface Award {
  award_name: string;
  issuing_organization: string;
  date: string;
}

interface ResumeData {
  personal_information: PersonalInfo;
  work_experience?: WorkExperience[];
  education?: Education[];
  skills?: Skills;
  projects?: Project[];
  awards_and_recognitions?: Award[];
}

// --- SVG Icons for Resume Viewer ---
const MailIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
  </svg>
);
const PhoneIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"
      clipRule="evenodd"
    />
  </svg>
);
const LinkedInIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
  </svg>
);
const PortfolioIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
      clipRule="evenodd"
    />
  </svg>
);
const DownloadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

// --- Resume Viewer Component ---
const ResumeViewer = ({ resumeJson }: { resumeJson: ResumeData | null }) => {
  if (!resumeJson) return null;

  const {
    personal_information: personal,
    work_experience: experience,
    education,
    skills,
    projects,
  } = resumeJson;

  const Section = ({
    title,
    children,
  }: {
    title: string;
    children: React.ReactNode;
  }) => (
    <div className="mt-8">
      <h2 className="text-2xl font-bold text-[rgb(143,17,166)] border-b-2 border-gray-200 pb-2 mb-4">
        {title}
      </h2>
      <div className="space-y-6">{children}</div>
    </div>
  );

  return (
    <div
      id="resume-to-download"
      className="bg-white shadow-xl rounded-lg w-full max-w-4xl p-6 sm:p-8 md:p-12"
      dir="rtl"
    >
      {/* --- Header --- */}
      <header className="flex flex-col sm:flex-row justify-between items-center mb-8">
        <div className="text-center sm:text-right mb-4 sm:mb-0">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-800">
            {personal.full_name}
          </h1>
          <p className="text-lg text-gray-500 mt-2">
            {experience?.[0]?.job_title || ""}
          </p>
        </div>
        <div className="flex flex-col items-center sm:items-end space-y-2 text-sm text-gray-600">
          {personal.email_address && (
            <div className="flex items-center gap-2">
              <p>{personal.email_address}</p> <MailIcon />
            </div>
          )}
          {personal.phone_number && (
            <div className="flex items-center gap-2">
              <p>{personal.phone_number}</p> <PhoneIcon />
            </div>
          )}
          {personal.linkedin_profile && (
            <div className="flex items-center gap-2">
              <a
                href={personal.linkedin_profile}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[rgb(143,17,166)]"
              >
                پروفایل لینکدین
              </a>{" "}
              <LinkedInIcon />
            </div>
          )}
          {personal.portfolio_url && (
            <div className="flex items-center gap-2">
              <a
                href={personal.portfolio_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[rgb(143,17,166)]"
              >
                وبسایت شخصی
              </a>{" "}
              <PortfolioIcon />
            </div>
          )}
        </div>
      </header>

      {/* --- Professional Summary --- */}
      {personal.professional_summary && (
        <Section title="خلاصه حرفه‌ای">
          <p className="text-gray-700 leading-relaxed">
            {personal.professional_summary}
          </p>
          {personal.ai_generated_short_summary && (
            <p className="text-gray-700 leading-relaxed mt-2 p-3 bg-purple-50 border-r-4 border-purple-400 rounded-md">
              <em>{personal.ai_generated_short_summary}</em>
            </p>
          )}
        </Section>
      )}

      {/* --- Work Experience --- */}
      {Array.isArray(experience) && experience.length > 0 && (
        <Section title="سوابق کاری">
          {experience.map((job, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div className="md:col-span-1 text-gray-500 text-sm">
                <p>
                  {job.start_date} – {job.end_date}
                </p>
                <p>{job.company_location}</p>
              </div>
              <div className="md:col-span-3">
                <h3 className="text-xl font-semibold text-gray-800">
                  {job.job_title}
                </h3>
                <p className="text-md text-gray-600 mb-2">{job.company_name}</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {job.key_responsibilities_and_achievements.map((resp, i) => (
                    <li key={i}>{resp}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* --- Education --- */}
      {Array.isArray(education) && education.length > 0 && (
        <Section title="تحصیلات">
          {education.map((edu, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div className="md:col-span-1 text-gray-500 text-sm">
                <p>{edu.graduation_date}</p>
                <p>{edu.institution_location}</p>
              </div>
              <div className="md:col-span-3">
                <h3 className="text-xl font-semibold text-gray-800">
                  {edu.degree_name}
                </h3>
                <p className="text-md text-gray-600 mb-1">
                  {edu.institution_name}
                </p>
                <p className="text-sm text-gray-500">{edu.field_of_study}</p>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* --- Skills --- */}
      {skills && (
        <Section title="مهارت‌ها">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.isArray(skills.technical) && skills.technical.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2 text-gray-700">فنی</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.technical.map((skill) => (
                    <span
                      key={skill}
                      className="bg-purple-100 text-purple-800 text-sm font-medium px-3 py-1 rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {Array.isArray(skills.soft) && skills.soft.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2 text-gray-700">نرم</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.soft.map((skill) => (
                    <span
                      key={skill}
                      className="bg-gray-100 text-gray-800 text-sm font-medium px-3 py-1 rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {Array.isArray(skills.languages) && skills.languages.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2 text-gray-700">زبان‌ها</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.languages.map((lang) => (
                    <span
                      key={lang.language_name}
                      className="bg-gray-100 text-gray-800 text-sm font-medium px-3 py-1 rounded-full"
                    >
                      {lang.language_name} ({lang.proficiency_level})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* --- Projects --- */}
      {Array.isArray(projects) && projects.length > 0 && (
        <Section title="پروژه‌ها">
          {projects.map((proj, index) => (
            <div key={index}>
              <h3 className="text-xl font-semibold text-gray-800">
                {proj.project_name}
              </h3>
              {proj.project_link && (
                <a
                  href={proj.project_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[rgb(143,17,166)] hover:underline"
                >
                  {proj.project_link}
                </a>
              )}
              <p className="text-gray-700 my-2">{proj.description}</p>
              {Array.isArray(proj.technologies_used) &&
                proj.technologies_used.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {proj.technologies_used.map((tech) => (
                      <span
                        key={tech}
                        className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
            </div>
          ))}
        </Section>
      )}
    </div>
  );
};

// Helper function to generate unique IDs
const generateId = () => "_" + Math.random().toString(36).substr(2, 9);

// Icons (React Functional Components)
const SendIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="w-5 h-5"
  >
    <path d="M3.105 3.105a.5.5 0 01.815-.086L19.5 9.5a.5.5 0 010 .998l-15.58 6.48a.5.5 0 01-.815-.912L18.09 10 3.105 3.914a.5.5 0 010-.809z" />
  </svg>
);
const BotAvatar = () => (
  <div className="w-8 h-8 rounded-full bg-[rgb(143,17,166)] flex items-center justify-center text-white text-sm font-semibold mr-2 flex-shrink-0">
    AI
  </div>
);
const UserAvatar = () => (
  <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-sm font-semibold mr-2 flex-shrink-0">
    شما
  </div>
);

const SYSTEM_PROMPT = `
حله! این یک محدودیت بسیار کاربردی برای متمرکز نگه داشتن گفتگو است. من این قانون جدید را به پرامپت اضافه می‌کنم. این قانون در بخش دستورالعمل‌های مکالمه قرار می‌گیرد تا رفتار ربات را در طول گفتگو کنترل کند.
پرامپت نهایی (نسخه جدید با محدودیت پاسخگویی):
شما "چت هوشمند کارجو پلاس" هستید، یک ربات چت هوش مصنوعی دوستانه و حرفه‌ای. هدف اصلی شما کمک به کاربر برای ساخت رزومه از طریق گفتگو است.
کاربر را قدم به قدم راهنمایی کنید و اطلاعات را یکی یکی یا دو تا دو تا بپرسید تا کاربر خسته نشود.
اطلاعاتی که باید جمع‌آوری کنید شامل موارد زیر است، اما محدود به اینها نیست:
 * اطلاعات شخصی:
   * نام کامل.
   * جنسیت: جنسیت فرد را از روی نام او تشخیص دهید. اگر نام رایج و مشخصی است (مانند محمد، علی، سارا، فاطمه)، فرض را بر همان بگذارید. اگر نامی است که می‌تواند برای هر دو جنس استفاده شود (مانند ماکان، تیام) یا شک دارید، حتماً جنسیت را از کاربر بپرسید.
   * تاریخ تولد.
   * وضعیت نظام وظیفه: پس از مشخص شدن جنسیت، فقط اگر کاربر مرد است، در مورد وضعیت نظام وظیفه (مانند پایان خدمت، معافیت، در حال انجام) سوال کنید.
   * شماره تلفن: تاکید کنید که باید شماره موبایل و دقیقاً ۱۱ رقمی باشد. اگر ورودی کاربر این شرایط را نداشت، با احترام از او بخواهید آن را اصلاح کند.
   * آدرس ایمیل.
   * URL پروفایل لینکدین (اختیاری).
   * URL پورتفولیو (اختیاری).
   * خلاصه/هدف حرفه‌ای (از کاربر بخواهید خود را به صورت حرفه‌ای توصیف کند یا اهداف شغلی خود را بیان کند).
 * سابقه کاری (برای هر نقش): عنوان شغلی، نام شرکت، مکان شرکت، تاریخ شروع، تاریخ پایان (یا "فعلی")، مسئولیت‌ها و دستاوردهای کلیدی (کاربر را به استفاده از لیست‌های نشانه‌دار یا جملات توصیفی تشویق کنید). برای چندین نقش قبلی سوال کنید.
 * تحصیلات (برای هر مدرک/صلاحیت): نام مدرک، رشته تحصیلی، نام موسسه، مکان موسسه، تاریخ فارغ‌التحصیلی (یا تاریخ مورد انتظار فارغ‌التحصیلی).
 * مهارت‌ها: مهارت‌های فنی (مانند زبان‌های برنامه‌نویسی، تسلط بر نرم‌افزار)، مهارت‌های نرم (مانند ارتباطات، کار تیمی)، زبان‌ها (گفتاری/نوشتاری، سطوح تسلط).
 * پروژه‌ها (اختیاری): نام پروژه، توضیحات مختصر، فناوری‌های مورد استفاده، لینک پروژه (در صورت وجود).
 * جوایز/تقدیرنامه‌ها (اختیاری): نام جایزه، نهاد اعطاکننده، تاریخ.
 * معرف‌ها: به طور مودبانه بیان کنید که معرف‌ها "در صورت درخواست ارائه می‌شوند" مگر اینکه کاربر صراحتاً بخواهد آنها را لیست کند.
دستورالعمل‌های گفتگو:
 * لحنی دوستانه و دلگرم‌کننده داشته باشید. از عباراتی مانند "عالیه!"، "جالب به نظر می‌رسه!"، "بعدش چیکار کردید؟"، "ممنون از اینکه به اشتراک گذاشتید." استفاده کنید.
 * اگر کاربر اطلاعاتی را خارج از ترتیب ارائه داد، آن را بپذیرید و سعی کنید آن را در بخش صحیح قرار دهید.
 * اگر ورودی کاربر مبهم یا ناقص بود، سوالات شفاف‌کننده بپرسید.
 * اگر کاربر درخواست اصلاح خلاصه یا پیشنهاد مسئولیت‌های شغلی را داشت، پیشنهادات مفیدی ارائه دهید.
 * محدودیت پاسخ به سوالات خارج از موضوع: هدف اصلی شما ساخت رزومه است. اگر کاربر سوالاتی پرسید که مستقیماً به اطلاعات مورد نیاز برای رزومه مرتبط نیست، می‌توانید به طور خلاصه پاسخ دهید. اما، پس از پاسخ دادن به حداکثر دو سوال خارج از موضوع، باید مودبانه و قاطعانه گفتگو را به فرآیند ساخت رزومه بازگردانید. برای مثال بگویید: "سوال خوبی است، اما برای اینکه روی هدف اصلی‌مان یعنی ساخت یک رزومه عالی متمرکز بمانیم، موافقید به ادامه جمع‌آوری اطلاعات بپردازیم؟"
وقتی فکر می‌کنید اطلاعات کافی دارید، یا اگر کاربر درخواست کرد، پیشنهاد دهید که رزومه را جمع‌آوری کنید.
بسیار مهم: دستورالعمل خروجی نهایی
اگر کاربر به وضوح درخواست "ساخت رزومه"، "تولید رزومه"، "رزومه را به من بده" یا عبارات مشابهی را داشت که نشان‌دهنده تمایل به دیدن خروجی نهایی رزومه است، پاسخ شما باید شامل دو بخش باشد:
۱. بخش اول: یک شیء JSON کامل و معتبر که حاوی تمام اطلاعات جمع‌آوری شده برای رزومه است.
- یک توضیح کوتاه و حرفه‌ای (در حد یک یا دو جمله) بر اساس کل اطلاعات دریافتی تولید کرده و در فیلد ai_generated_short_summary قرار دهید.
- نکته حیاتی در مورد فرمت: خروجی JSON حتماً باید در یک بلاک کد قرار گیرد. این بلاک باید با \`\`\`json (سه بک‌تیک و کلمه json بدون فاصله بین آنها) در خط اول شروع شده و با \`\`\` (سه بک‌تیک) در خط آخر پایان یابد. هیچ کاراکتر یا فاصله‌ای نباید قبل از \`\`\`json در خط شروع و بعد از \`\`\` در خط پایان وجود داشته باشد. بین خط شروع و پایان نیز نباید هیچ کاراکتر اضافه‌ای غیر از محتوای JSON وجود داشته باشد. کلیدهای JSON باید انگلیسی باشند. این قانون به هیچ وجه نباید نادیده گرفته شود.

مثال ساختار JSON (فیلدها بر اساس اطلاعات جمع‌آوری شده تکمیل شوند):
\`\`\`json
{
  "personal_information": {
    "full_name": "مقدار فارسی",
    "gender": "مقدار فارسی (مرد/زن)",
    "date_of_birth": "مقدار فارسی",
    "military_service_status": "مقدار فارسی (فقط برای مردان، در غیر این صورت حذف شود)",
    "phone_number": "مقدار فارسی (باید ۱۱ رقم باشد)",
    "email_address": "مقدار فارسی",
    "linkedin_profile": "مقدار فارسی (اختیاری)",
    "portfolio_url": "مقدار فارسی (اختیاری)",
    "professional_summary": "مقدار فارسی که کاربر ارائه داده",
    "ai_generated_short_summary": "یک خلاصه کوتاه تولید شده توسط هوش مصنوعی بر اساس کل داده‌ها"
  },
  "work_experience": [
    {
      "job_title": "مقدار فارسی",
      "company_name": "مقدار فارسی",
      "company_location": "مقدار فارسی",
      "start_date": "مقدار فارسی",
      "end_date": "مقدار فارسی",
      "key_responsibilities_and_achievements": ["مورد فارسی ۱", "مورد فارسی ۲"]
    }
  ],
  "education": [
    {
      "degree_name": "مقدار فارسی",
      "field_of_study": "مقدار فارسی",
      "institution_name": "مقدار فارسی",
      "institution_location": "مقدار فارسی",
      "graduation_date": "مقدار فارسی"
    }
  ],
  "skills": {
    "technical": ["مهارت فارسی ۱", "مهارت فارسی ۲"],
    "soft": ["مهارت فارسی ۱", "مهارت فارسی ۲"],
    "languages": [{"language_name": "نام زبان فارسی", "proficiency_level": "سطح تسلط فارسی"}]
  },
  "projects": [
    {
      "project_name": "مقدار فارسی",
      "description": "مقدار فارسی",
      "technologies_used": ["فناوری فارسی ۱"],
      "project_link": "مقدار فارسی (اختیاری)"
    }
  ],
  "awards_and_recognitions": [
    {
      "award_name": "مقدار فارسی",
      "issuing_organization": "مقدار فارسی",
      "date": "مقدار فارسی"
    }
  ],
  "references_statement": "مقدار فارسی (مثلاً: در صورت درخواست ارائه می‌شود)"
}
\`\`\`
۲. بخش دوم: بلافاصله پس از بلاک کد JSON، محتوای کامل رزومه ساختاریافته با فرمت Markdown را ارائه دهید. فرمت Markdown باید شامل سرفصل‌ها (مانند ## سابقه کاری) و لیست‌های نشانه‌دار برای مسئولیت‌ها و مهارت‌ها باشد و اطلاعات جدید مانند تاریخ تولد و وضعیت نظام وظیفه را نیز شامل شود.
در این حالت خاص (تولید رزومه)، هیچ متن محاوره‌ای دیگری قبل از بلاک JSON یا بعد از رزومه Markdown اضافه نکنید. کل پاسخ باید فقط شامل این دو بخش باشد.
در غیر این صورت (در طول مکالمه عادی)، به صورت محاوره‌ای پاسخ دهید.`;

type Message = { id: string; sender: string; text: string };

export const ResumeChatDeepseekWithShow = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: generateId(),
      sender: "bot",
      text: "سلام! من چت هوشمند کارجو پلاس هستم و می‌توانم به شما در ساخت یک رزومه حرفه‌ای کمک کنم. ما این کار را با گفتگو در مورد تجربیات، تحصیلات و مهارت‌های شما انجام خواهیم داد. برای شروع آماده‌اید؟",
    },
  ]);
  const [userInput, setUserInput] = useState("");
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isResumeGenerated, setIsResumeGenerated] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [generatedResumeJson, setGeneratedResumeJson] =
    useState<ResumeData | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    if (isResumeGenerated) {
      const jspdfScriptId = "jspdf-script";
      const html2canvasScriptId = "html2canvas-script";

      // Load scripts only if they don't already exist
      if (!document.getElementById(jspdfScriptId)) {
        const jspdfScript = document.createElement("script");
        jspdfScript.id = jspdfScriptId;
        jspdfScript.src =
          "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
        jspdfScript.async = true;
        document.body.appendChild(jspdfScript);
      }

      if (!document.getElementById(html2canvasScriptId)) {
        const html2canvasScript = document.createElement("script");
        html2canvasScript.id = html2canvasScriptId;
        html2canvasScript.src =
          "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
        html2canvasScript.async = true;
        document.body.appendChild(html2canvasScript);
      }
    }
  }, [isResumeGenerated]);

  const callDeepSeekApi = async (
    userMessageText: string,
    conversationHistory: { sender: string; text: string }[]
  ) => {
    setIsBotTyping(true);

    const messagesForApi = [
      { role: "system", content: SYSTEM_PROMPT },
      ...conversationHistory.map((msg) => ({
        role: msg.sender === "bot" ? "assistant" : "user",
        content: msg.text,
      })),
      { role: "user", content: userMessageText },
    ];

    const payload = {
      model: "deepseek-chat",
      messages: messagesForApi,
      temperature: 0.1,
      max_tokens: 4096,
    };

    try {
      const response = await fetch(
        "https://api.deepseek.com/chat/completions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer sk-5f62986117d14b289a1eb88122ed60fb`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `API request failed: ${errorData?.error?.message || "Unknown error"}`
        );
      }

      const result = await response.json();
      if (result.choices?.[0]?.message?.content) {
        return result.choices[0].message.content;
      } else {
        throw new Error("Failed to get valid response from AI");
      }
    } catch (error) {
      console.error("Error calling DeepSeek API:", error);
      return "متاسفانه در ارتباط با دستیار هوشمند مشکلی پیش آمد. لطفاً دوباره امتحان کنید.";
    } finally {
      setIsBotTyping(false);
    }
  };

  const addMessage = (sender: string, text: string) => {
    setMessages((prev) => [...prev, { id: generateId(), sender, text }]);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userInput.trim() || isBotTyping) return;

    const newUserMessage = {
      id: generateId(),
      sender: "user",
      text: userInput,
    };
    setMessages((prevMessages) => [...prevMessages, newUserMessage]);
    const currentInput = userInput;
    setUserInput("");

    const aiResponseText = await callDeepSeekApi(currentInput, messages);

    const jsonBlockRegex = /```json\s*([\s\S]*?)\s*```/;
    const jsonMatch = aiResponseText.match(jsonBlockRegex);

    if (jsonMatch && jsonMatch[1]) {
      const jsonString = jsonMatch[1];
      try {
        const parsedJson = JSON.parse(jsonString);
        setGeneratedResumeJson(parsedJson);
        setIsResumeGenerated(true); // Switch to resume view
      } catch (error) {
        console.error(
          "Failed to parse JSON from AI response:",
          error,
          "\nJSON string was:",
          jsonString
        );
        addMessage(
          "bot",
          "متاسفانه در پردازش اطلاعات رزومه (بخش JSON) از پاسخ هوش مصنوعی مشکلی پیش آمد. لطفاً دوباره امتحان کنید."
        );
      }
    } else {
      addMessage("bot", aiResponseText);
    }

    if (!jsonMatch) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleDownloadPdf = () => {
    const resumeElement = document.getElementById("resume-to-download");
    // @ts-ignore
    if (!resumeElement || !window.jspdf || !window.html2canvas) {
      alert(
        "کتابخانه ساخت PDF هنوز بارگذاری نشده است. لطفاً چند لحظه صبر کرده و دوباره امتحان کنید."
      );
      return;
    }
    setIsDownloading(true);

    // @ts-ignore
    const { jsPDF } = window.jspdf;
    // @ts-ignore
    const html2canvas = window.html2canvas;

    // Clone the node to render it off-screen and avoid screen jump
    const clone = resumeElement.cloneNode(true) as HTMLElement;
    clone.style.position = "absolute";
    clone.style.left = "-9999px";
    clone.style.top = "0px";
    clone.style.width = "1024px"; // Force desktop width for consistent PDF layout
    document.body.appendChild(clone);

    html2canvas(clone, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
    })
      .then((canvas: any) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        // Calculate the height of the image in the PDF to maintain aspect ratio
        const ratio = canvasWidth / pageWidth;
        const canvasHeightInPDF = canvasHeight / ratio;

        let heightLeft = canvasHeightInPDF;
        let position = 0;

        // Add the first page
        pdf.addImage(imgData, "PNG", 0, position, pageWidth, canvasHeightInPDF);
        heightLeft -= pageHeight;

        // Loop and add new pages if content is longer than one page
        while (heightLeft > 20) {
          // Add a small margin check
          position -= pageHeight;
          pdf.addPage();
          pdf.addImage(
            imgData,
            "PNG",
            0,
            position,
            pageWidth,
            canvasHeightInPDF
          );
          heightLeft -= pageHeight;
        }

        pdf.save(
          `${
            generatedResumeJson?.personal_information.full_name || "resume"
          }.pdf`
        );
      })
      .catch((err: any) => {
        console.error("Could not generate PDF", err);
        alert("مشکلی در ساخت فایل PDF پیش آمد. لطفاً دوباره تلاش کنید.");
      })
      .finally(() => {
        // Cleanup: remove the clone and reset state
        document.body.removeChild(clone);
        setIsDownloading(false);
      });
  };

  if (isResumeGenerated) {
    return (
      <div className="font-sans bg-gray-100 min-h-screen flex flex-col items-center justify-start p-4 py-8">
        <ResumeViewer resumeJson={generatedResumeJson} />
        <div className="mt-8 w-full max-w-4xl flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DownloadIcon />
            {isDownloading ? "در حال ساخت PDF..." : "دانلود PDF"}
          </button>
          <button
            onClick={() => {
              setIsResumeGenerated(false);
              setMessages([
                {
                  id: generateId(),
                  sender: "bot",
                  text: "عالیه! بیایید یک رزومه جدید بسازیم یا رزومه قبلی را ویرایش کنیم. از کجا شروع کنیم؟",
                },
              ]);
              setGeneratedResumeJson(null);
            }}
            className="flex-1 bg-[rgb(143,17,166)] hover:bg-[rgb(120,15,140)] text-white font-semibold py-3 px-4 rounded-lg transition"
          >
            شروع مجدد / بازگشت به چت
          </button>
        </div>
      </div>
    );
  }

  const formatMessageContent = (text: string) => {
    const processBoldText = (line: string) => {
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return parts.map((part, index) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={index}>{part.slice(2, -2)}</strong>
        ) : (
          part
        )
      );
    };

    if (text.includes("- ") || text.includes("* ")) {
      return text
        .split("\n")
        .map((line, i) =>
          line.startsWith("- ") || line.startsWith("* ") ? (
            <li key={i}>{processBoldText(line.substring(2))}</li>
          ) : (
            <p key={i}>{processBoldText(line)}</p>
          )
        );
    }
    return text.split("\n").map((line, i) => (
      <p key={i} className="mb-2 last:mb-0">
        {processBoldText(line)}
      </p>
    ));
  };

  return (
    <div
      className="font-sans bg-gradient-to-br from-slate-900 to-slate-800 min-h-screen flex flex-col items-center justify-center p-2 sm:p-4"
      dir="rtl"
    >
      <style>{`::selection { background-color: rgb(143, 17, 166); color: white; }`}</style>
      <div
        className="bg-white/90 backdrop-blur-md shadow-2xl rounded-xl w-full max-w-lg flex flex-col"
        style={{ height: "calc(100vh - 2rem)", maxHeight: "700px" }}
      >
        <header className="p-4 border-b border-gray-200 bg-[rgb(143,17,166)] rounded-t-xl">
          <h1 className="text-xl font-bold text-white text-center">
            چت هوشمند کارجو پلاس
          </h1>
          <p className="text-xs text-gray-200 text-center">
            دستیار شخصی شما برای ساخت رزومه ✨
          </p>
        </header>

        <main
          dir="ltr"
          className="flex-grow p-4 space-y-4 overflow-y-auto bg-gray-50"
        >
          {messages.map((msg) => {
            const isUser = msg.sender === "user";
            const alignmentClass = isUser ? "justify-end" : "justify-start";
            const bubbleTailClasses = isUser
              ? "rounded-br-none"
              : "rounded-bl-none";

            return (
              <div
                key={msg.id}
                className={`flex items-end mb-2 ${alignmentClass}`}
              >
                {!isUser && <BotAvatar />}
                <div
                  className={`max-w-[75%] p-3 rounded-xl shadow ${bubbleTailClasses} ${
                    isUser
                      ? "bg-[rgb(143,17,166)] text-white"
                      : "bg-white text-gray-700 border border-gray-200"
                  } ml-2 mr-2`}
                  style={{ direction: "rtl" }}
                >
                  <div className="text-sm whitespace-pre-wrap">
                    {!isUser ? formatMessageContent(msg.text) : msg.text}
                  </div>
                </div>
                {isUser && <UserAvatar />}
              </div>
            );
          })}
          {isBotTyping && (
            <div className="flex items-end justify-start mb-2">
              <BotAvatar />
              <div className="max-w-[75%] p-3 rounded-xl shadow bg-white text-gray-700 border border-gray-200 rounded-bl-none ml-2 mr-2">
                <div
                  className="flex items-center space-x-1"
                  style={{ direction: "ltr" }}
                >
                  <div className="w-2 h-2 bg-[rgb(143,17,166)] rounded-full animate-pulse delay-75"></div>
                  <div className="w-2 h-2 bg-[rgb(143,17,166)] rounded-full animate-pulse delay-150"></div>
                  <div className="w-2 h-2 bg-[rgb(143,17,166)] rounded-full animate-pulse delay-200"></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </main>

        <form
          onSubmit={handleSendMessage}
          className="p-4 border-t border-gray-200 bg-white rounded-b-xl"
        >
          <div className="flex items-center space-x-2 flex-row-reverse">
            <input
              ref={inputRef}
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder={
                isBotTyping
                  ? "هوش مصنوعی در حال پردازش است..."
                  : "پیام خود را تایپ کنید..."
              }
              className="flex-grow p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(143,17,166)] outline-none text-sm text-right"
              disabled={isBotTyping || isResumeGenerated}
              style={{ direction: "rtl" }}
            />
            <button
              type="submit"
              className="bg-[rgb(143,17,166)] hover:bg-[rgb(120,15,140)] text-white p-3 rounded-lg disabled:opacity-50"
              disabled={isBotTyping || !userInput.trim() || isResumeGenerated}
            >
              <SendIcon />
            </button>
          </div>
        </form>
      </div>
      <footer className="text-center mt-4">
        <p className="text-xs text-gray-400">قدرت گرفته از کارجوپلاس</p>
      </footer>
    </div>
  );
};
